import React, { memo } from 'react';
import { FunctionField, ReferenceField, SelectField, TextField, useTranslate } from 'react-admin';
import { Box, Link, makeStyles, Typography, useTheme } from '@material-ui/core';
import moment from 'moment';
import { Empty } from 'antd';
import resourceSlug from '../../../../constant/resource-slug';
import DrawerSimpleShowLayout from '../../../../base/components/ra/drawers/DrawerSimpleShowLayout';
import { AmountField, DateField } from '../../../../base/components/ra/fields';
import Labeled from '../../../../base/components/ra/labeled';
import useGetPaymentDetails from '../../hooks/useGetPaymentDetails';
import DetailsSkeleton from '../../../../base/components/common/DetailsSkeleton';
import CustomLinkField from '../../../../base/components/ra/fields/customLink.field';
import PaymentStatus from '../PaymentStatus';
import { DATE_DISPLAY_FORMAT, DATETIME_DISPLAY_FORMAT } from '../../../../constant';
import ArrayWithPaginationField from '../../../../base/components/ra/fields/arrayWithPagination.field';
import { usePaymentIdSync } from '../../hooks/usePaymentIdSync';
import { formatDate } from '../../../../services/util/date';
import { PAYMENT_METHOD, PAYMENT_STATUS } from '../../../../constant/invoice';
import ProofsPreview from '../ProofPreview';

const PaymentDetails = memo(() => {
  const theme = useTheme();
  const classes = useStyles();
  const t = useTranslate();
  const { paymentId } = usePaymentIdSync();
  const { payment: record, loading, error } = useGetPaymentDetails(paymentId);

  if (loading) {
    return (
      <DetailsSkeleton
        sx={{
          mt: 6,
        }}
      />
    );
  }

  if (!record || error) {
    return (
      <Box mt={6}>
        <Empty description={false} />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 2em)"
      sx={{
        overflowY: 'auto',
      }}
      mt={6}
    >
      <Box
        component={DrawerSimpleShowLayout}
        sx={{
          flexGrow: 1,
          overflow: 'scroll',
        }}
        record={record}
      >
        <Box
          component={Typography}
          variant="h4"
          sx={{
            marginBottom: '10px !important',
          }}
        >
          {t('common.text.generalInfo')}
        </Box>
        <TextField source="id" label="resources.payment.fields.id" />
        <ReferenceField
          label="resources.group.name"
          sortable={false}
          reference={resourceSlug.GROUP}
          source="payment.groupId"
        >
          <TextField source="name" />
        </ReferenceField>
        <Labeled label={t('resources.payment.fields.createdTime')}>
          <DateField source="payment.createdAt" dateFormat={DATETIME_DISPLAY_FORMAT} />
        </Labeled>
        <Labeled label={t('resources.payment.fields.completedTime')}>
          <DateField source="payment.completedAt" dateFormat={DATETIME_DISPLAY_FORMAT} />
        </Labeled>
        <SelectField
          source="payment.paymentMethod"
          label="resources.payment.fields.paymentMethod"
          choices={Object.values(PAYMENT_METHOD).map((method) => ({
            id: method,
            name: t(`common.paymentMethod.${method}`),
          }))}
        />
        <Labeled label={t('resources.payment.fields.amount')}>
          <AmountField source="payment.amount" symbol={record?.payment?.currencyCode} />
        </Labeled>

        <Labeled label={t('resources.payment.fields.payer')}>
          <CustomLinkField
            source="payment.payer"
            component={record?.payment?.payerId ? Link : Box}
            href={`/#/${resourceSlug.USER}/${record?.payment?.payerId}/show`}
            title={record?.payment?.payerUsername}
          />
        </Labeled>
        <TextField source="payment.description" label="resources.payment.fields.description" emptyText="-" />
        <FunctionField
          label="resources.payment.fields.proofs"
          render={(record) => <ProofsPreview justifyContent="flex-end" urls={record?.payment?.paymentProofUrls} />}
        />
        <FunctionField
          label="resources.payment.fields.status"
          render={() => (
            <Box display="flex" justifyContent="flex-end">
              <PaymentStatus status={record?.payment?.status} />
            </Box>
          )}
        />
        <Box
          component={Typography}
          variant="h4"
          sx={{
            margin: '10px 0 !important',
          }}
        >
          {t('resources.payment.invoiceInfo')}
        </Box>
        <ArrayWithPaginationField
          label={false}
          source="invoices"
          rowClick={(id) => `/${resourceSlug.INVOICE}/${id}/show`}
        >
          <TextField
            sortable={false}
            source="invoiceNo"
            label="resources.invoice.fields.invoiceNo"
            cellClassName={classes.invoiceNo}
          />
          <DateField
            source="invoiceDate"
            label="resources.invoice.fields.invoiceDate"
            dateFormat={DATE_DISPLAY_FORMAT}
            sx={{
              minWidth: 90,
            }}
            sortable={false}
          />
          <FunctionField
            label="resources.invoice.fields.dueDate"
            dateFormat={DATE_DISPLAY_FORMAT}
            sortable={false}
            cellClassName={classes.date}
            render={(record) =>
              record.dueDate ? (
                <Box
                  color={
                    moment.unix(record.dueDate).isSameOrBefore(moment()) && record.paymentStatus !== PAYMENT_STATUS.PAID
                      ? theme.palette.error.main
                      : undefined
                  }
                >
                  {formatDate(record.dueDate, DATE_DISPLAY_FORMAT)}
                </Box>
              ) : (
                '-'
              )
            }
          />
          <FunctionField
            label="resources.invoice.fields.invoicePeriod"
            cellClassName={classes.invoicePeriod}
            sortable={false}
            render={(record) =>
              `${formatDate(record.invoiceFrom, DATE_DISPLAY_FORMAT)} - ${formatDate(record.invoiceTo, DATE_DISPLAY_FORMAT)}`
            }
          />
          <AmountField
            source="invoiceAmount"
            label="resources.invoice.fields.amount"
            sx={{
              minWidth: 150,
            }}
            symbol={(record) => record.currencyCode}
            sortable={false}
          />
        </ArrayWithPaginationField>
      </Box>
    </Box>
  );
});

export default PaymentDetails;

const useStyles = makeStyles({
  invoicePeriod: {
    minWidth: 200,
  },
  invoiceNo: {
    minWidth: 200,
  },
  date: {
    minWidth: 90,
  },
});
